import { InfoCard } from '@backstage/core-components';
import { Icon } from '@iconify/react';
import { Typography } from '@mui/material';
import {
  type ComponentProps,
  type FC,
  isValidElement,
  type PropsWithChildren,
  useCallback,
} from 'react';
import { tss } from 'tss-react/mui';

import type { Icon as IconType } from '../types';

const useStyles = tss.create(({ theme }) => ({
  icon: {
    fontSize: 40,
    height: 40,
    width: 40,
  },
  title: {
    flex: 1,
    marginBottom: 0,
    marginTop: 0,
  },
  titleContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
  },
}));

type Props = Pick<ComponentProps<typeof InfoCard>, 'action' | 'className'> & {
  icon?: IconType;
  iconScale?: number;
  title: string;
};

export const DetailTile: FC<PropsWithChildren<Props>> = ({
  children,
  icon,
  iconScale = 1,
  title,
  ...passThroughProps
}) => {
  const { classes } = useStyles();

  const getIconStyle = useCallback(
    () => (iconScale ? { transform: `scale(${iconScale})` } : undefined),
    [iconScale],
  );

  let iconElement = null;
  if (isValidElement(icon)) {
    iconElement = icon;
  } else if (typeof icon === 'string') {
    iconElement = (
      <Icon className={classes.icon} icon={icon} style={getIconStyle()} />
    );
  }

  const titleElement = (
    <div className={classes.titleContainer}>
      {iconElement}
      <Typography className={classes.title} variant="h4">
        {title}
      </Typography>
    </div>
  );
  return (
    <section aria-label={`${title} Tile`}>
      <InfoCard title={titleElement} {...passThroughProps} divider={false}>
        {children}
      </InfoCard>
    </section>
  );
};
